<script lang="ts">
    /**
     * LandingApp - 홈페이지 메인 레이아웃 + 라우터 (앱 셸)
     *
     * 앱바(LandingNav) + 푸터(LandingFooter)는 한 번 마운트되어 고정.
     * 가운데 컨텐츠 영역만 클라이언트 라우팅으로 교체 (리로드 없음).
     * - /           → 회사 허브 (CompanyHome) — 멀티 프로덕트 진입점
     * - /jadeye     → JadEye 제품 소개 (HomePage)
     * - /gridverse  → Gridverse 제품 소개 (GridversePage)
     * - /pin        → Pin 제품 소개 (PinPage) — PIN_ENABLED 일 때만 존재
     * - /ink        → Ink 대문 (InkPage) — INK_ENABLED 일 때만 존재(기본 켜짐)
     * - /guide      → 사용자 가이드 (GuidePage)
     *
     * 📌 각 서비스의 "제품 소개" 는 전부 이 셸 안의 한 페이지다(D9). 실제 서비스는 셸 밖에 있다
     *    (/web·/gv 는 각자 SPA, /pin/rest-area 는 SSR). 새 서비스도 이 목록에 한 줄이면 편입된다.
     */
    import LandingNav from './components/LandingNav.svelte';
    import LandingFooter from './components/LandingFooter.svelte';
    import ClickmonAd from './components/ClickmonAd.svelte';
    import HomePage from './pages/HomePage.svelte';
    import GuidePage from './pages/GuidePage.svelte';
    import CompanyHome from './pages/CompanyHome.svelte';
    import GridversePage from './pages/GridversePage.svelte';
    import PinPage from './pages/PinPage.svelte';
    import InkPage from './pages/InkPage.svelte';
    import { getPin } from '../../lib/pin';
    import { getInk } from '../../lib/ink';

    let currentPath = $state(window.location.pathname);

    // 📍 꺼진 환경에서는 null → /pin 이 SPA 경로 목록에서도 빠진다(서버에도 라우트가 없다).
    const pin = getPin();
    // 🖋️ Ink 도 같다 — 꺼지면 null. 서가 · 작품 홈(/ink/works/*)은 서버 렌더라 여기 넣지 않는다.
    const ink = getInk();

    // 클라이언트 라우팅이 처리하는 SPA 경로 (그 외 /web·/privacy·/terms 등은 풀 네비게이션)
    // ⚠️ Pin 의 **콘텐츠**(/pin/rest-area)는 여기 넣지 않는다 — 서버 렌더 페이지라
    //    가로채면 빈 화면이 된다. 여기 있는 건 소개 페이지(/pin) 하나뿐이다.
    const SPA_ROUTES = ['/', '/jadeye', '/gridverse', '/guide', ...(pin ? [pin.landing] : []), ...(ink ? [ink.landing] : [])];

    type SeoEntry = { title: string; description: string; canonical: string };

    /**
     * 경로별 메타 갱신. 출처는 서버가 내려준 window.__SEO__ (config/seo.php ← SSOT).
     * 각 페이지가 <svelte:head> 로 title 을 따로 선언하던 것을 대체한다 — 서버·클라 값이 갈렸었다.
     *
     * og:* 는 일부러 건드리지 않는다. 카카오·트위터 스크래퍼는 JS 를 실행하지 않고
     * SPA 내부 클릭을 따라오지도 않으므로, 클라에서 갱신해봐야 읽는 주체가 없다.
     * 첫 로드 시점의 서버 렌더 og 태그가 곧 정답이다.
     */
    function applyMeta(path: string) {
        const seo = (window as unknown as { __SEO__?: Record<string, SeoEntry> }).__SEO__?.[path];
        if (!seo) return;

        document.title = seo.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', seo.description);
        document.querySelector('link[rel="canonical"]')?.setAttribute('href', seo.canonical);
    }

    /**
     * SPA 이동을 GA4 에 알린다 (D19, 2026-09-09).
     *
     * 🚨 이게 없으면 랜딩의 이동이 **통째로 유실된다.** gtag('config', …) 가 보내는 page_view 는
     *    최초 1회뿐인데, 이 셸은 pushState 로 /jadeye·/gridverse·/pin·/guide 를 오간다
     *    (SPA_ROUTES). 즉 "회사 홈에 들어와서 무엇을 눌렀는가" 가 정확히 안 보이는 축이다.
     *
     * ⚠️ 반드시 applyMeta() **뒤에** 부른다 — page_title 을 document.title 에서 읽는데,
     *    applyMeta 가 그 값을 바꾸기 때문이다. 순서가 뒤집히면 **이전 페이지 제목**이 실린다.
     *
     * 🎛 GA 가 꺼져 있으면(측정ID 미설정·시행일 전·GA_ENABLED=false) window.gtag 자체가 없다.
     *    그래서 조용히 no-op 한다 — 여기서 던지면 라우팅이 통째로 죽는다.
     */
    function trackPageView(path: string) {
        const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
        if (typeof gtag !== 'function') return;

        gtag('event', 'page_view', {
            page_location: window.location.origin + path,
            page_path: path,
            page_title: document.title,
        });
    }

    function navigate(path: string) {
        if (path === currentPath) {
            // 같은 페이지 메뉴 재클릭 → 맨 위로
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        history.pushState({}, '', path);
        currentPath = path;
        applyMeta(path);
        trackPageView(path);
        window.scrollTo({ top: 0 });
    }

    // 내부 링크 클릭을 가로채 컨텐츠만 교체 (앱바/푸터는 고정).
    // 이벤트 위임 → nav·footer·카드 등 모든 내부 <a>가 자동으로 클라 라우팅됨.
    function handleClick(e: MouseEvent) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const anchor = (e.target as HTMLElement)?.closest?.('a');
        if (!anchor) return;
        if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
        const href = anchor.getAttribute('href');
        // 해시(#)·외부(http)·mailto·SPA 외부 경로(/web 등)는 기본 동작 유지
        if (!href || !SPA_ROUTES.includes(href)) return;
        e.preventDefault();
        navigate(href);
    }

    $effect(() => {
        // 브라우저 뒤로/앞으로가기 동기화
        const onPop = () => {
            currentPath = window.location.pathname;
            applyMeta(currentPath);
            trackPageView(currentPath);
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    });
</script>

<svelte:head>
    <!-- Pretendard는 landing.blade.php에서 CDN 로드 -->
    <!-- title·description·canonical 은 서버(landing.blade.php)가 SSOT. SPA 이동 시 applyMeta() 가 갱신. -->
</svelte:head>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div class="landing-app" onclick={handleClick}>
    <LandingNav {currentPath} />

    <main class="landing-main">
        {#if currentPath === '/guide'}
            <GuidePage />
        {:else if currentPath === '/jadeye'}
            <HomePage />
        {:else if currentPath === '/gridverse'}
            <GridversePage />
        {:else if pin && currentPath === pin.landing}
            <PinPage />
        {:else if ink && currentPath === ink.landing}
            <InkPage />
        {:else}
            <CompanyHome />
        {/if}
    </main>

    <!-- 💰 클릭몬 배너(D20) — 셸에 한 번. 꺼져 있으면(시행일 전·dev·로컬) 아무것도 그리지 않는다.
         🖋️ Ink 대문에서는 그리지 않는다 — 「광고가 없어요」가 그 화면이 하는 약속이다(숨기는 게 아니라 만들지 않는다). -->
    {#if !(ink && currentPath === ink.landing)}
        <ClickmonAd />
    {/if}

    <LandingFooter {currentPath} />
</div>

<style>
    .landing-app {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        font-family: 'Instrument Sans', 'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;

        /* Landing 전용 브랜드 컬러 (CSO 문서 기준) */
        --landing-primary: #1E5C3A;
        --landing-secondary: #B49B50;
        --landing-cta: #FF6B35;
        --landing-bg: #FFFFFF;
        --landing-bg-alt: #F8F9FA;
        --landing-text: #1A1A1A;
        --landing-text-sub: #666666;
        --landing-error: #D32F2F;
        --landing-dark: #1A1A1A;
    }

    .landing-main {
        flex: 1;
    }
</style>
